import React, { useEffect, useState } from "react";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Box from "@material-ui/core/Box";
import useTheme from "@material-ui/core/styles/useTheme";

import Accordion from "components/Accordion";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import { TabTable } from "components/TabTables/TabTables";
import { useRacehorse360Api } from "hooks/api";
import PaperCarousel from "../../PaperCarousel";
import { SortOrder } from "interfaces/SortOrder";
import useStyles from "./styles";

interface IScratch {
  id: number;
  date: Date;
  race: string;
  track: string;
  reason: string;
}

export interface OwnProps extends TabTable {
  pagination?: boolean;
  scratches?: IScratch[];
  isLoading?: boolean;
  horse: racehorse360.IHorse;
  rootRef: React.RefObject<HTMLElement>;
  isSliderOnMobile?: boolean;
}

type QueryResponse = {
  isLoading: any;
  data: any;
};

const PAGE_SIZE = 7;
const SLIDE_SIZE = 5;

const Scratches = (props: OwnProps) => {
  const { pagination, onLoad, horse, rootRef, isSliderOnMobile } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isSliderView = !pagination && isSliderOnMobile;
  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"));

  const [activePage, setActivePage] = useState<number>(1);
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [raceSlides, setRaceSlides] = useState<racehorse360.IRaceHistory[][]>(
    []
  );

  const { useListRaceHistories } = useRacehorse360Api();

  const request = {
    query: {
      horse: horse.id,
      isScratched: {
        value: true
      }
    },
    pagingOptions: {
      maxResults: isSliderView ? SLIDE_SIZE : PAGE_SIZE,
      offset: isSliderView
        ? activeSlide * SLIDE_SIZE
        : (activePage - 1) * PAGE_SIZE,
      includeSummary: true
    },
    getOptions: {
      select: [
        "scratch_reason",
        "horse",
        "date",
        "facility.code",
        "race_number"
      ],
      orderBy: [`date ${SortOrder.DESC}`]
    }
  };

  const { isLoading, data }: QueryResponse = useListRaceHistories(
    "scratchHistory",
    request,
    {
      onSuccess: ({ raceHistories, pagingInfo }) => {
        if (isSliderView) {
          const slides = isInitialLoad
            ? new Array(Math.ceil(pagingInfo.totalResults / SLIDE_SIZE)).fill(
                null
              )
            : [...raceSlides];

          slides[activeSlide] = raceHistories;

          setRaceSlides(slides);
          setIsInitialLoad(false);
        }
        onLoad && onLoad(raceHistories, pagingInfo);
      },
      onError: error => console.error(error)
    }
  );

  const { raceHistories = [], pagingInfo: { totalResults = 0 } = {} } =
    data || {};

  const from = pagination ? 0 : activeSlide * SLIDE_SIZE + 1;
  const to =
    !pagination && activeSlide * SLIDE_SIZE + SLIDE_SIZE < totalResults
      ? activeSlide * SLIDE_SIZE + SLIDE_SIZE
      : totalResults;

  useEffect(() => {
    if (data?.raceHistories && onLoad) {
      onLoad(data?.raceHistories, data?.pagingInfo);
    }
  }, []);

  const handleSlideChange = index => {
    setActiveSlide(index);
  };

  const renderTableHead = () => {
    return (
      <TableRow className={classes.tableHead}>
        <TableCell className={classes.tableHeadCell}>Date-Race-Track</TableCell>
        <TableCell className={classes.tableHeadCell}>Reason</TableCell>
      </TableRow>
    );
  };

  const renderRows = items => {
    const sliced = isSliderView ? items : raceHistories;

    if (!sliced || (!isSliderView && isLoading)) {
      return <Loader className={classes.slideLoader} />;
    }

    return sliced.map(scratch => {
      return (
        <TableRow key={scratch?.id} className={classes.scratchRow}>
          <TableCell className={classes.scratchCell}>
            {`${format(parseISO(scratch.date), "dLLLyy")}-${
              scratch.raceNumber
            }-${scratch.facility.code}`}
          </TableCell>
          <TableCell className={classes.scratchCell}>
            {scratch.scratchReason}
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderTable = (items = null) => {
    return raceHistories.length || isLoading ? (
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableBody>
            {renderTableHead()}
            {renderRows(items)}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <div className={classes.emptyDataHolder}>No Scratches</div>
    );
  };

  if (isLoading && pagination) {
    return <Loader className={classes.slideLoader} />;
  }

  return matchesUpMd ? (
    <>
      {renderTable()}
      {raceHistories?.length > 0 && (
        <Pagination
          pageNumber={activePage}
          totalCount={totalResults}
          pageSize={PAGE_SIZE}
          onPageChange={setActivePage}
        />
      )}
    </>
  ) : (
    <Box className={classes.tableWrapper}>
      <Accordion
        disabled={(!raceHistories?.length && !isLoading) || isLoading}
        count={totalResults}
        className={classes.accordion}
        isLoading={isLoading}
        name={"Scratches"}
        rootRef={rootRef}
        hideBackground
        hideShadow
        roundCount
        hideSubtitleWhenCollapsed
        showCountLoader
        subtitle={
          raceHistories?.length ? (
            `Showing ${from}-${to} of ${totalResults}`
          ) : (
            <Loader size={16} />
          )
        }
      >
        <PaperCarousel
          items={raceSlides}
          itemRenderer={renderTable}
          onSlideChange={handleSlideChange}
        />
      </Accordion>
    </Box>
  );
};

export default React.memo(Scratches);
